/**
 * Event Illustrations - Keyword-triggered emoji for diary event blocks.
 * Inspired by Google Calendar's secret illustrations.
 * Scans event/holiday titles for keywords and returns a matching emoji.
 */

const KEYWORD_MAP = {
  // ── Countries & Destinations ──
  spain: '🇪🇸',
  ibiza: '🏝️',
  mallorca: '🏝️',
  france: '🇫🇷',
  paris: '🗼',
  italy: '🇮🇹',
  rome: '🏛️',
  greece: '🇬🇷',
  turkey: '🇹🇷',
  dubai: '🏙️',
  america: '🇺🇸',
  usa: '🇺🇸',
  'new york': '🗽',
  mexico: '🇲🇽',
  thailand: '🇹🇭',
  bali: '🏝️',
  japan: '🇯🇵',
  australia: '🇦🇺',
  portugal: '🇵🇹',
  germany: '🇩🇪',
  ireland: '🇮🇪',
  scotland: '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
  caribbean: '🏝️',
  maldives: '🏝️',
  hawaii: '🌺',
  vegas: '🎰',
  amsterdam: '🇳🇱',
  croatia: '🇭🇷',
  morocco: '🇲🇦',
  egypt: '🏛️',
  india: '🇮🇳',
  canada: '🇨🇦',
  brazil: '🇧🇷',
  switzerland: '🇨🇭',

  // ── Travel & Holiday ──
  beach: '🏖️',
  holiday: '✈️',
  vacation: '🌴',
  flight: '✈️',
  travel: '✈️',
  trip: '🧳',
  abroad: '🌍',
  cruise: '🚢',
  camping: '⛺',
  skiing: '⛷️',
  ski: '⛷️',
  snowboard: '🏂',
  hotel: '🏨',
  resort: '🏖️',
  'road trip': '🚗',
  backpacking: '🎒',
  safari: '🦁',
  hiking: '🥾',

  // ── Sports & Activities ──
  gym: '💪',
  football: '⚽',
  soccer: '⚽',
  rugby: '🏉',
  tennis: '🎾',
  golf: '⛳',
  swimming: '🏊',
  swim: '🏊',
  running: '🏃',
  run: '🏃',
  cycling: '🚴',
  boxing: '🥊',
  yoga: '🧘',
  pilates: '🧘',
  crossfit: '🏋️',
  basketball: '🏀',
  cricket: '🏏',
  surfing: '🏄',
  climbing: '🧗',
  'martial arts': '🥋',
  dance: '💃',
  ballet: '🩰',
  horse: '🐴',
  riding: '🐴',
  fishing: '🎣',

  // ── Shopping & Errands ──
  shopping: '🛍️',
  groceries: '🛒',
  supermarket: '🛒',
  ikea: '🪑',
  amazon: '📦',
  delivery: '📦',
  'post office': '📮',
  bank: '🏦',
  car: '🚗',
  mot: '🔧',
  garage: '🔧',
  mechanic: '🔧',
  vet: '🐾',
  optician: '👓',
  pharmacy: '💊',
  haircut: '💇',

  // ── Health & Wellness ──
  dentist: '🦷',
  doctor: '🩺',
  hospital: '🏥',
  clinic: '🏥',
  spa: '💆',
  massage: '💆',
  physio: '🦴',
  therapy: '🧠',
  counselling: '🧠',
  counseling: '🧠',
  meditation: '🧘',
  acupuncture: '📍',
  chiropractor: '🦴',
  scan: '🏥',
  'blood test': '💉',
  vaccine: '💉',
  checkup: '🩺',

  // ── Food & Drink ──
  breakfast: '🥐',
  brunch: '🥂',
  lunch: '🍽️',
  dinner: '🍷',
  supper: '🍽️',
  coffee: '☕',
  tea: '🍵',
  drinks: '🍻',
  cocktails: '🍸',
  wine: '🍷',
  pub: '🍺',
  restaurant: '🍽️',
  takeaway: '🥡',
  pizza: '🍕',
  sushi: '🍣',
  bbq: '🍖',
  barbecue: '🍖',
  picnic: '🧺',
  cake: '🍰',
  baking: '🧁',

  // ── Social & Celebrations ──
  birthday: '🎂',
  party: '🎉',
  wedding: '💒',
  engagement: '💍',
  anniversary: '💕',
  'baby shower': '👶',
  'hen do': '👰',
  'stag do': '🎩',
  christening: '⛪',
  graduation: '🎓',
  prom: '👗',
  reunion: '🤗',
  'date night': '💕',
  cinema: '🎬',
  film: '🎬',
  movie: '🎬',
  theatre: '🎭',
  theater: '🎭',
  concert: '🎵',
  gig: '🎸',
  festival: '🎪',
  museum: '🏛️',
  gallery: '🎨',
  exhibition: '🖼️',

  // ── Work & Professional ──
  meeting: '👥',
  conference: '🎤',
  training: '📚',
  course: '🎓',
  workshop: '🛠️',
  seminar: '📊',
  interview: '💼',
  review: '📋',
  presentation: '📊',
  deadline: '⏰',
  networking: '🤝',
  'team building': '🏗️',
  offsite: '🏕️',
  webinar: '💻',
  demo: '📺',
  launch: '🚀',
  shoot: '📸',

  // ── Family & Personal ──
  school: '🏫',
  nursery: '🧒',
  kids: '👶',
  childcare: '👶',
  playdate: '🧒',
  'parents evening': '🏫',
  'sports day': '🏅',
  nativity: '⭐',
  moving: '📦',
  house: '🏠',
  flat: '🏠',
  viewing: '🏠',
  solicitor: '📝',
  lawyer: '⚖️',
  'estate agent': '🏠',
  diy: '🔨',
  garden: '🌱',
  decorating: '🎨',
  painting: '🖌️',
  sick: '🤒',
  unwell: '🤒',
  maternity: '🤰',
  paternity: '👶',
  funeral: '🕊️',
  memorial: '🕊️',
  'wedding dress': '👰',

  // ── Salon & Beauty Industry ──
  photoshoot: '📸',
  photo: '📸',
  competition: '🏆',
  awards: '🏆',
  stock: '📦',
  stocktake: '📦',
  cleaning: '✨',
  'deep clean': '🧹',
  inventory: '📦',
  audit: '📋',
  inspection: '📋',
  'hair show': '💇',
  colour: '🎨',
  color: '🎨',
  education: '📚',
  supplier: '📦',
  'rep visit': '🤝',
  model: '💃',
  refurb: '🔨',
  renovation: '🔨',
  opening: '🎉',
  closing: '🔒',

  // ── Breaks & General ──
  break: '☕',
  personal: '🏠',
  admin: '💻',
  call: '📞',
  phone: '📱',
  email: '📧',
  appointment: '📅',
  reminder: '🔔',
  errand: '🏃',
  volunteer: '❤️',
  charity: '❤️',
  church: '⛪',
  mosque: '🕌',
  temple: '🛕',
  prayer: '🙏',
  christmas: '🎄',
  easter: '🐣',
  halloween: '🎃',
  valentines: '❤️',
  'new year': '🎆',
  bonfire: '🔥',
  thanksgiving: '🦃',
  eid: '🌙',
  diwali: '🪔',
  hanukkah: '🕎',
  ramadan: '🌙',
};

// Pre-compute sorted keywords (longest first for best match)
const SORTED_KEYWORDS = Object.keys(KEYWORD_MAP).sort((a, b) => b.length - a.length);

/**
 * Match an event/holiday title to an emoji.
 * Returns the emoji string or null if no match.
 */
export function matchEventEmoji(title) {
  if (!title) return null;
  const lower = title.toLowerCase();
  for (const keyword of SORTED_KEYWORDS) {
    if (lower.includes(keyword)) {
      return KEYWORD_MAP[keyword];
    }
  }
  return null;
}

/**
 * Colour presets for the colour picker.
 */
export const COLOUR_PRESETS = [
  '#29282D', // charcoal
  '#007EA0', // teal
  '#E85D75', // coral
  '#D6D5EC', // lavender
  '#8DB580', // sage
  '#E8A838', // amber
  '#6B7B8D', // slate
  '#E8B4C8', // rose
  '#C8EEEE', // mint
  '#f5dabf', // peach
];
